import { supabase } from '../../lib/supabase'
import { CONFIG } from '../../config/config'

const getMonday = (d = new Date()) => {
  const day  = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  const mon  = new Date(d)
  mon.setDate(diff)
  mon.setHours(0, 0, 0, 0)
  return mon.toISOString().split('T')[0]
}

export const reviewSlice = (set, get) => ({
  reviews:   [],
  reviewDue: false,

  loadReviews: async () => {
    const uid = get().user?.id
    if (!uid) return
    try {
      const { data, error } = await supabase
        .from('weekly_reviews').select('*').eq('user_id', uid)
        .order('week_start', { ascending: false })
      if (!error) {
        set({ reviews: data ?? [] })
        get().checkReviewDue()
      }
    } catch (e) {
      console.warn('[Mori] loadReviews error:', e.message)
    }
  },

  checkReviewDue: () => {
    const { reviews, profile } = get()
    if (!profile?.onboarding_completed_at) return set({ reviewDue: false })
    const onboarded = new Date(profile.onboarding_completed_at)
    if ((Date.now() - onboarded) / 86400000 < 7) return set({ reviewDue: false })
    if (!reviews.length) return set({ reviewDue: true })
    const last      = new Date(reviews[0].week_start)
    const daysSince = (Date.now() - last) / 86400000
    set({ reviewDue: daysSince >= CONFIG.REVIEW_CYCLE_DAYS })
  },

  submitReview: async ({ actualHours, notes }) => {
    const uid      = get().user?.id
    const week     = getMonday()
    const goals    = get().goals

    // FIX: Snapshot planned hours at submission time so historical reviews
    // don't change when current goals change
    const plannedHoursSnapshot = goals
      .filter(g => g.status === 'active')
      .reduce((acc, g) => ({ ...acc, [g.id]: g.weekly_hours ?? 0 }), {})

    const payload = {
      user_id:       uid,
      week_start:    week,
      actual_hours:  actualHours ?? {},
      planned_hours: plannedHoursSnapshot,   // snapshot — immutable after save
      notes:         notes ?? '',
      completed_at:  new Date().toISOString(),
    }

    try {
      const { data, error } = await supabase
        .from('weekly_reviews')
        .upsert(payload, { onConflict: 'user_id,week_start' })
        .select().single()

      if (!error) {
        set(s => ({
          reviews:   [data, ...s.reviews.filter(r => r.week_start !== week)],
          reviewDue: false,
        }))
        await get().checkUnlocks()
      }
      return { data, error }
    } catch (e) {
      return { error: { message: e.message } }
    }
  },

  // Uses planned_hours snapshot from stored review — historically accurate
  consistencyScore: () => {
    const { reviews } = get()
    if (reviews.length < CONFIG.MIN_REVIEWS_FOR_TREND) return null

    let consistent = 0
    let counted    = 0

    reviews.forEach(r => {
      const planned = Object.values(r.planned_hours ?? {}).reduce((s, h) => s + Number(h), 0)
      const actual  = Object.values(r.actual_hours  ?? {}).reduce((s, h) => s + Number(h), 0)
      if (planned > 0) {
        counted++
        if (actual >= planned * CONFIG.CONSISTENCY_TARGET) consistent++
      }
    })

    return counted ? Math.round((consistent / counted) * 100) : null
  },

  // Uses snapshot from last review — stable regardless of current goals
  plannedVsActual: () => {
    const { reviews } = get()
    if (!reviews.length) return null
    const r       = reviews[0]
    const planned = Object.values(r.planned_hours ?? {}).reduce((s, h) => s + Number(h), 0)
    const actual  = Object.values(r.actual_hours  ?? {}).reduce((s, h) => s + Number(h), 0)
    return { planned, actual, variance: actual - planned }
  },

  estimationAccuracy: () => {
    const done = get().phases.filter(p =>
      p.status === 'completed' && p.actual_weeks && p.estimated_weeks
    )
    if (!done.length) return null
    const avg = done.reduce((s, p) => s + (p.actual_weeks / p.estimated_weeks), 0) / done.length
    return { ratio: Math.min(avg, 2.5), count: done.length }
  },
})
